/**
 * Gem placement pipeline for hd_neo.
 *
 * Ported from hd_conf's placeGemsAndSettings.tsx + helpers.
 *
 * Coordinate system: SCENE_SCALE = 0.2 → 1 mm = 0.2 Three.js units.
 * All gem dimensions from the API are in mm and must be multiplied by SCENE_SCALE.
 *
 * DOUBLE-ROTATION FIX:
 *   The ring is rendered inside `<group rotation={...}>` in RingScene.tsx.
 *   We raycast against a *temporary* Mesh built from ringMesh.geometry only
 *   (never added to the scene, so matrixWorld = identity). Intersection points
 *   and normals come back in the ring's LOCAL space. The group rotation then
 *   applies uniformly to both ring and gems — no double-rotation.
 *
 * Supports:
 *   STARFIELD layout — each gem placed at a custom (offsetX, offsetY) position
 *   LEGACY / DISTRIBUTED — gems evenly spaced around the ring circumference
 *   FIXED spacing — gems placed with a fixed mm gap between them
 *
 * Does NOT perform boolean mesh subtraction (preview-only).
 */

import {
    BufferGeometry,
    CatmullRomCurve3,
    MathUtils,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    Quaternion,
    Raycaster,
    Vector3,
} from 'three';
import { Brush, Evaluator, SUBTRACTION } from 'three-bvh-csg';
import { mergeBufferGeometries } from 'three-stdlib';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { ApiGem, ApiGemSpacing, ApiLayoutDetails, ApiSettingType } from '../../types/api';
import { createGemMaterial } from './createGemMaterial';
import { innerRadiusFromSize } from './ringExtrusion';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Matches hd_neo's ring extrusion scale: 1 mm → 0.2 Three.js units */
const SCENE_SCALE = 0.2;

const mmToScene = (mm: number): number => mm * SCENE_SCALE;

/**
 * Number of rays cast around the ring for the outer-surface curve.
 * Matches hd_conf's staticConfig.sceneRingParams.numberOfRays.
 */
const NUM_RAYS = 70;

/** Small depth offset so gems sit flush with the ring surface (matching hd_conf's 0.025/1000 m) */
const SETTING_DEPTH = 0.025 * SCENE_SCALE; // 0.025 mm in scene units

/**
 * How far the CSG cutter extends OUTWARD beyond the ring surface.
 * Ensures the pocket opening has the full girdle-width cross-section visible
 * at the surface rather than just the narrow table face of the gem.
 * Value in scene units (1 mm = 0.2 su).
 */
const CSG_PROTRUSION = mmToScene(0.15); // 0.5 mm protrusion

// ─── GLTF loading ─────────────────────────────────────────────────────────────

export interface GemGltfMeshes {
    ROUND: Mesh | null;
    RECTANGULAR: Mesh | null;
    SQUARED: Mesh | null;
    OVAL: Mesh | null;
}

let _gemGltfPromise: Promise<GemGltfMeshes> | null = null;

/** Loads gems.glb once, returns a map of shape → Mesh (raw, unscaled) */
export function loadGemGltf(): Promise<GemGltfMeshes> {
    if (_gemGltfPromise) return _gemGltfPromise;

    _gemGltfPromise = new Promise((resolve, reject) => {
        const loader = new GLTFLoader();
        const draco = new DRACOLoader();
        draco.setDecoderPath('/3d/draco/');
        loader.setDRACOLoader(draco);

        loader.load(
            '/3d/gems.glb',
            (gltf) => {
                const result: GemGltfMeshes = { ROUND: null, RECTANGULAR: null, SQUARED: null, OVAL: null };
                gltf.scene.traverse((node) => {
                    if (!(node as Mesh).isMesh) return;
                    const mesh = node as Mesh;
                    if (node.name === 'brill_65x10ct') result.ROUND = mesh;
                    else if (node.name === 'bag_60x30') result.RECTANGULAR = mesh;
                    else if (node.name === 'princ_10x10') result.SQUARED = mesh;
                    else if (node.name === 'oval_5x3_5') result.OVAL = mesh;
                });
                resolve(result);
            },
            undefined,
            (err) => reject(new Error(`[loadGemGltf] ${err}`)),
        );
    });

    return _gemGltfPromise;
}

// ─── Gem geometry scaling ──────────────────────────────────────────────────────

export interface GemMeasurements {
    /** Cloned, rotated, scaled geometry — X = depth axis, Y = inline, Z = perpendicular */
    geometry: BufferGeometry;
    /** Height of the gem above its pivot in scene units (used to sink into ring) */
    gemHeightAbovePivot: number;
    /** XYZ bounding-box size in scene units */
    size: Vector3;
}

/**
 * Returns a correctly scaled gem geometry for a given gem API record.
 * Matches hd_conf's getInitialScaledGem() logic.
 */
export function buildGemGeometry(shape: string, gemData: ApiGem, rawMeshes: GemGltfMeshes): GemMeasurements {
    const shapeKey = shape.toUpperCase() as keyof GemGltfMeshes;
    const sourceMesh = rawMeshes[shapeKey] ?? rawMeshes.ROUND;
    if (!sourceMesh) throw new Error('[buildGemGeometry] No gem mesh found for shape: ' + shape);

    const geo = sourceMesh.geometry.clone();

    // Remove all buffer attributes except position and normal to enable
    // clean merging via mergeBufferGeometries (matches hd_conf's cullBufferAttributes).
    for (const name of Object.keys(geo.attributes)) {
        if (name !== 'position' && name !== 'normal') geo.deleteAttribute(name);
    }

    // Rotate so that outward axis = +X (matching hd_conf's coordinate convention)
    geo.rotateZ(MathUtils.degToRad(-90));
    geo.computeBoundingBox();

    const bb = geo.boundingBox!;
    const startingLength = bb.max.y - bb.min.y; // inline with ring
    const startingWidth = bb.max.z - bb.min.z;  // perpendicular to ring
    const startingDepth = bb.max.x - bb.min.x;  // gem depth

    // API returns dimensions as decimal strings (e.g. "0.9") even though typed as number
    const toMM = (v: number | string | null | undefined, fallback = 1): number =>
        parseFloat(String(v ?? fallback)) || fallback;

    // API gives mm; convert to scene units
    const targetLength = mmToScene(toMM(gemData.length ?? gemData.diameter));
    const targetWidth  = mmToScene(toMM(gemData.width  ?? gemData.diameter));
    const targetDepth  = mmToScene(toMM(gemData.height));

    let sX: number, sY: number, sZ: number;
    if (shapeKey === 'ROUND') {
        const ratio = targetLength / startingLength;
        sX = ratio; sY = ratio; sZ = ratio;
    } else {
        sY = targetLength / startingLength;
        sZ = targetWidth  / startingWidth;
        sX = targetDepth  / startingDepth;
    }

    geo.scale(sX, sY, sZ);
    geo.computeBoundingBox();

    const size = new Vector3();
    geo.boundingBox!.getSize(size);

    return {
        geometry: geo,
        gemHeightAbovePivot: geo.boundingBox!.max.x, // +X is outward
        size,
    };
}

// ─── Raycasting ───────────────────────────────────────────────────────────────

/**
 * Raycasts inward from a circle of radius `outerRadius` at Z=rotationPoint.z
 * to find intersection points and normals on the ring's actual outer surface.
 *
 * Ported directly from hd_conf's calculateOuterCurve().
 *
 * LOCAL-SPACE TRICK:
 *   We raycast against a *temporary* Mesh built only from ringMesh.geometry,
 *   never added to the scene. Its matrixWorld is the default identity matrix,
 *   so the Raycaster operates entirely in geometry-local space. Intersection
 *   points and normals are therefore in the ring's local coordinate system.
 *
 *   The ring in RingScene.tsx is wrapped in `<group rotation={...}>`. If we
 *   raycasted against the actual ringMesh (which has the group rotation baked
 *   into its matrixWorld), gem positions would include that rotation, and the
 *   group would rotate them again when rendering — double-rotation bug.
 *   Using the temp mesh avoids this entirely.
 *
 * SCALE NOTE:
 *   `outerRadius` is passed in mm (e.g. innerRadius_mm + ringHeight_mm ≈ 10.6).
 *   The ring geometry is in scene units (1 mm = 0.2 su, so outer edge ≈ 2.1 su).
 *   Rays originate at ~10.6 su — well outside the ring — and shoot toward
 *   rotationPoint, passing through the actual curved surface. This matches
 *   hd_conf's behaviour where outerRadius_mm (~10.6) >> ring_meters (~0.011).
 */
function calculateOuterCurve(
    ringMesh: Mesh,
    outerRadius: number,       // mm — used directly as ray-origin radius in scene space
    rotationPoint: Vector3,    // scene units, local space
): { intersectionPoints: Vector3[]; intersectionNormals: Vector3[] } {
    // Temp mesh with identity matrixWorld — raycaster results are in local space.
    const tempMesh = new Mesh(ringMesh.geometry, new MeshBasicMaterial());
    if (!tempMesh.geometry.boundingSphere) tempMesh.geometry.computeBoundingSphere();

    const raycaster = new Raycaster();
    const intersectionPoints: Vector3[] = [];
    const intersectionNormals: Vector3[] = [];
    const defaultUp = new Vector3(0, 1, 0);
    const angleStep = (2 * Math.PI) / NUM_RAYS;

    for (let i = 0; i < NUM_RAYS; i++) {
        const angle = i * angleStep;
        const x = rotationPoint.x + Math.cos(angle) * outerRadius;
        const y = rotationPoint.y + Math.sin(angle) * outerRadius;
        const rayOrigin = new Vector3(x, y, rotationPoint.z);
        const rayDirection = rotationPoint.clone().sub(rayOrigin).normalize();
        raycaster.set(rayOrigin, rayDirection);

        const hits = raycaster.intersectObject(tempMesh, false);
        if (hits.length) {
            const hit = hits[0];
            intersectionPoints.push(hit.point);
            intersectionNormals.push(hit.normal ?? defaultUp.clone());
        }
    }

    // Fallback: if raycasting missed (e.g. very thin profile at this Z), use radial circle.
    if (intersectionPoints.length === 0) {
        const rSu = mmToScene(outerRadius);
        for (let i = 0; i < NUM_RAYS; i++) {
            const angle = i * angleStep;
            const cosA = Math.cos(angle);
            const sinA = Math.sin(angle);
            intersectionPoints.push(new Vector3(
                rotationPoint.x + cosA * rSu,
                rotationPoint.y + sinA * rSu,
                rotationPoint.z,
            ));
            intersectionNormals.push(new Vector3(cosA, sinA, 0));
        }
    }

    return { intersectionPoints, intersectionNormals };
}

// ─── Placement types ──────────────────────────────────────────────────────────

export interface GemPlacement {
    position: Vector3;
    up: Vector3;
    rotation: Quaternion;
}

// ─── Distribute gems along a raycasted curve ──────────────────────────────────

const corrY = new Quaternion().setFromAxisAngle(new Vector3(0, -1, 0), Math.PI / 2);
const corrX = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI / 2);

function buildPlacementFromCurve(
    curve: CatmullRomCurve3,
    normals: Vector3[],
    t: number,
    rotationPoint: Vector3,
): GemPlacement {
    const point = curve.getPointAt(t);
    const tangent = curve.getTangentAt(t).normalize();

    const total = normals.length;
    const idx = t * (total - 1);
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, total - 1);
    const normal = new Vector3().lerpVectors(normals[i0], normals[i1], idx - i0);

    const up = new Vector3().subVectors(point, rotationPoint).normalize();
    const binormal = new Vector3().crossVectors(normal, tangent).normalize();
    const rotMat = new Matrix4().makeBasis(tangent, binormal, normal);
    const rotation = new Quaternion().setFromRotationMatrix(rotMat);
    rotation.multiply(corrY).multiply(corrX);

    return { position: point, up, rotation };
}

/**
 * Distributes gem placements along the ring's outer surface curve.
 * Handles DISTRIBUTED and FIXED spacing options.
 */
function distributeAlongCurve(
    curve: CatmullRomCurve3,
    normals: Vector3[],
    rotationPoint: Vector3,
    count: number,
    spacing: ApiGemSpacing,
    gemSize: Vector3, // in scene units
): GemPlacement[] {
    const totalLength = curve.getLength();
    const gemLength = gemSize.y; // Y = inline dimension
    const minDist = mmToScene(spacing.minProductionDistance ?? 0.1);
    const placements: GemPlacement[] = [];

    if (spacing.option.toUpperCase() === 'DISTRIBUTED') {
        const u_step = 1 / count;
        for (let i = 0; i < count; i++) {
            try {
                const u = Math.min(Math.max((i + u_step) / count, 0), 1);
                placements.push(buildPlacementFromCurve(curve, normals, u, rotationPoint));
            } catch { /* skip bad samples */ }
        }
    } else {
        // FIXED
        let spacingDist: number;
        if (spacing.spacingUnit.toUpperCase() === 'GEM') {
            spacingDist = spacing.distance * gemLength;
        } else {
            spacingDist = Math.max(mmToScene(spacing.distance), minDist);
        }
        const step = gemLength + spacingDist;
        const maxGems = Math.floor(totalLength / step);
        const safeCount = Math.min(count, Math.max(maxGems, 1));
        const uStep = step / totalLength;
        for (let i = 0; i < safeCount; i++) {
            try {
                const u = Math.min(i * uStep, 1);
                placements.push(buildPlacementFromCurve(curve, normals, u, rotationPoint));
            } catch { /* skip bad samples */ }
        }
    }

    return placements;
}

// ─── Starfield placement ───────────────────────────────────────────────────────

/**
 * Places gems at custom (offsetX, offsetY) positions from a starfield layout_details.
 * offsetY = axial (Z) offset in mm, offsetX = circumferential offset in mm.
 * Matches hd_conf's STARFIELD branch in calculateGemAndSettingDistribution().
 */
async function buildStarfieldPlacements(
    ringMesh: Mesh,
    layoutDetails: ApiLayoutDetails,
    outerRadiusMm: number,    // mm — passed directly as ray-origin radius (matches hd_conf)
    gemsByid: Map<string, ApiGem>,
    rawMeshes: GemGltfMeshes,
): Promise<Array<{ placement: GemPlacement; measurements: GemMeasurements; shape: string }>> {
    const entries = layoutDetails.entries ?? [];
    const results: Array<{ placement: GemPlacement; measurements: GemMeasurements; shape: string }> = [];

    // Build raycasted outer-surface curves in LOCAL space, one per unique Z offset.
    // LOCAL SPACE: calculateOuterCurve uses a temp mesh with identity matrixWorld,
    // so all intersection points/normals are in the ring's local coordinate system.
    // The scene group rotation then applies uniformly to both ring and gems.
    const uniqueZOffsets = Array.from(new Set(entries.map((e) => mmToScene(e.offsetY))));
    const curvesAtZ = new Map<number, { curve: CatmullRomCurve3; normals: Vector3[] }>();

    for (const z of uniqueZOffsets) {
        const rp = new Vector3(0, 0, z);
        const { intersectionPoints, intersectionNormals } = calculateOuterCurve(ringMesh, outerRadiusMm, rp);
        if (intersectionPoints.length < 2) continue;
        const curve = new CatmullRomCurve3(intersectionPoints, true, 'centripetal');
        curve.arcLengthDivisions = 300;
        curve.updateArcLengths();
        curvesAtZ.set(z, { curve, normals: intersectionNormals });
    }

    for (const entry of entries) {
        const z = mmToScene(entry.offsetY);
        const data = curvesAtZ.get(z);
        if (!data) continue;

        const { curve, normals } = data;
        const rp = new Vector3(0, 0, z);
        const xSceneOffset = mmToScene(Math.abs(entry.offsetX));
        const t = Math.min(xSceneOffset / curve.getLength(), 1);
        const placement = buildPlacementFromCurve(curve, normals, t, rp);

        // Use the gem referenced by gemId, falling back to any available gem
        const gemData = gemsByid.get(entry.gemId) ?? gemsByid.values().next().value;
        if (!gemData) continue;

        const shape = gemData.cut?.shape ?? 'ROUND';
        try {
            const measurements = buildGemGeometry(shape, gemData, rawMeshes);
            results.push({ placement, measurements, shape });
        } catch { /* skip */ }
    }

    return results;
}

// ─── Main entry point ──────────────────────────────────────────────────────────

export interface GemPlacementResult {
    /** Merged mesh of all visible gems */
    gemMesh: Mesh;
    /**
     * Gem surface positions in ring local-object space, one per placed gem.
     * Kept for compatibility; no longer written to shader uniforms since pockets are physical.
     */
    gemPositions: Vector3[];
    /**
     * Per-gem pocket radius in scene units: gem.diameter/2 + 0.1mm converted to scene units.
     */
    gemRadii: number[];
    /**
     * Ring geometry with gem pockets boolean-subtracted.
     * Replace ringMesh.geometry with this to show physical pockets;
     * restore the original on unmount.
     */
    modifiedRingGeometry: BufferGeometry;
}

/**
 * Picks the best setting type:
 *   1. First STARFIELD layout type (any style)
 *   2. First FLUSH style type
 *   3. First available setting type
 */
export function pickSettingType(settingTypes: ApiSettingType[]): ApiSettingType | null {
    if (!settingTypes.length) return null;
    return (
        settingTypes.find((s) => s.layoutType.toUpperCase() === 'STARFIELD') ??
        settingTypes.find((s) => s.styleType.toUpperCase() === 'FLUSH') ??
        settingTypes[0]
    );
}

// ─── CSG helper ───────────────────────────────────────────────────────────────

/**
 * Subtracts gem volumes from the ring geometry using three-bvh-csg.
 *
 * Both geometries must be in the same coordinate space (ring local).
 * `gemsGeo` may be a merged geometry representing multiple gems.
 *
 * Returns a new BufferGeometry; caller is responsible for disposing it.
 */
function carveGemsIntoRing(ringGeo: BufferGeometry, gemsGeo: BufferGeometry): BufferGeometry {
    const evaluator = new Evaluator();
    evaluator.useGroups = false;
    // Gem geometries have only position+normal (uv is stripped in buildGemGeometry).
    // three-bvh-csg defaults to attributes=['position','uv','normal']; restrict to the
    // common subset so it doesn't crash on the missing uv attribute.
    evaluator.attributes = ['position', 'normal'];

    const ringBrush = new Brush(ringGeo);
    const gemBrush  = new Brush(gemsGeo);
    ringBrush.updateMatrixWorld();
    gemBrush.updateMatrixWorld();
    return evaluator.evaluate(ringBrush, gemBrush, SUBTRACTION).geometry;
}

/**
 * Full gem placement pipeline.
 * Raycasts the ring mesh to find the outer surface, then places gem instances.
 * Returns a merged Mesh of all visible gems, ready to add to the scene.
 */
export async function buildGemMeshes(params: {
    ringMesh: Mesh;
    settingType: ApiSettingType;
    /** Ring size (circumference) in mm */
    ringSize: number;
    /** Ring height in mm (radial thickness) */
    ringHeight: number;
    /** Gem count override (used for LEGACY/FLUSH layouts) */
    gemCount?: number;
}): Promise<GemPlacementResult | null> {
    const { ringMesh, settingType, ringSize, ringHeight, gemCount } = params;

    // Gems for this setting come from allowedGems (filtered by cut/dimensions),
    // NOT from ring_partial.gems (which is empty for most starfield rings).
    const gems = settingType.allowedGems ?? [];
    if (!gems.length) return null;

    const rawMeshes = await loadGemGltf();

    const innerRadius = innerRadiusFromSize(ringSize);  // mm
    // outerRadiusMm is passed directly as the ray-origin radius to calculateOuterCurve.
    // The ring geometry is in scene units (~2.1 su outer) but the ray from ~10.6 su
    // still hits it — matching hd_conf's approach exactly.
    const outerRadiusMm = innerRadius + ringHeight;     // mm

    // Build gem map — API returns dimensions as decimal strings; parseFloat handles coercion.
    const gemsByid = new Map(gems.map((g) => [g.id, g]));
    const isStarfield = settingType.layoutType.toUpperCase() === 'STARFIELD';
    const layoutDetails = settingType.layoutDetails;

    // ── STARFIELD ────────────────────────────────────────────────────────────
    if (isStarfield && layoutDetails?.entries?.length) {
        const placements = await buildStarfieldPlacements(
            ringMesh, layoutDetails, outerRadiusMm, gemsByid, rawMeshes,
        );
        if (!placements.length) return null;

        const geoArr: BufferGeometry[] = [];
        const csgArr: BufferGeometry[] = [];
        for (const { placement, measurements } of placements) {
            const g = applyGemTransform(measurements, placement);
            if (g) geoArr.push(g);
            const c = applyGemCutterTransform(measurements, placement);
            if (c) csgArr.push(c);
        }

        if (!geoArr.length) return null;
        const merged = mergeBufferGeometries(geoArr) as BufferGeometry;
        geoArr.forEach((g) => g.dispose());
        const mergedCutter = csgArr.length ? mergeBufferGeometries(csgArr) as BufferGeometry : merged;
        csgArr.forEach((g) => g.dispose());
        const modifiedRingGeometry = carveGemsIntoRing(ringMesh.geometry, mergedCutter);
        if (mergedCutter !== merged) mergedCutter.dispose();
        const gemMaterial = createGemMaterial(merged);

        // Collect positions/radii (kept for callers that may still inspect them).
        const gemPositions = placements.map((p) => p.placement.position.clone());
        const gemRadii = placements.map((p) => (p.measurements.size.y / 2) + (0.1 * SCENE_SCALE));

        const gemMesh = new Mesh(merged, gemMaterial);
        return { gemMesh, gemPositions, gemRadii, modifiedRingGeometry };
    }

    // ── LEGACY / FLUSH ────────────────────────────────────────────────────────
    const firstGem = gems[0];
    const shape = firstGem.cut?.shape ?? 'ROUND';
    let measurements: GemMeasurements;
    try {
        measurements = buildGemGeometry(shape, firstGem, rawMeshes);
    } catch {
        return null;
    }

    const rotationPoint = new Vector3(0, 0, 0);
    const { intersectionPoints, intersectionNormals } = calculateOuterCurve(ringMesh, outerRadiusMm, rotationPoint);
    if (intersectionPoints.length < 2) return null;

    const curve = new CatmullRomCurve3(intersectionPoints, true, 'centripetal');
    curve.arcLengthDivisions = 300;
    curve.updateArcLengths();

    // Pick spacing — prefer DISTRIBUTED, fall back to first available
    const spacing =
        settingType.spacings.find((s) => s.option.toUpperCase() === 'DISTRIBUTED') ??
        settingType.spacings[0];

    if (!spacing) return null;

    // Derive gem count: use provided override, or estimate from spacing + ring circumference
    const totalLength = curve.getLength();
    const gemLength = measurements.size.y;
    const minDist = mmToScene(spacing.minProductionDistance ?? 0.1);
    const effectiveSpacing = spacing.spacingUnit.toUpperCase() === 'GEM'
        ? spacing.distance * gemLength
        : Math.max(mmToScene(spacing.distance), minDist);
    const autoCount = Math.max(1, Math.floor(totalLength / (gemLength + effectiveSpacing)));
    const count = gemCount ?? autoCount;

    const placements = distributeAlongCurve(curve, intersectionNormals, rotationPoint, count, spacing, measurements.size);
    if (!placements.length) return null;

    const geoArr: BufferGeometry[] = [];
    const csgArr: BufferGeometry[] = [];
    for (const placement of placements) {
        const g = applyGemTransform(measurements, placement);
        if (g) geoArr.push(g);
        const c = applyGemCutterTransform(measurements, placement);
        if (c) csgArr.push(c);
    }

    if (!geoArr.length) return null;
    const merged = mergeBufferGeometries(geoArr) as BufferGeometry;
    geoArr.forEach((g) => g.dispose());
    const mergedCutter = csgArr.length ? mergeBufferGeometries(csgArr) as BufferGeometry : merged;
    csgArr.forEach((g) => g.dispose());
    const modifiedRingGeometry = carveGemsIntoRing(ringMesh.geometry, mergedCutter);
    if (mergedCutter !== merged) mergedCutter.dispose();
    const gemMaterial = createGemMaterial(merged);

    const gemPositions = placements.map((p) => p.position.clone());
    const gemRadii = placements.map(() => (measurements.size.y / 2) + (0.1 * SCENE_SCALE));

    const gemMesh = new Mesh(merged, gemMaterial);
    return { gemMesh, gemPositions, gemRadii, modifiedRingGeometry };
}

// ─── Transform helper ──────────────────────────────────────────────────────────

/**
 * Clones the gem geometry and applies position + rotation for a single placement.
 * Matches hd_conf's transformTmpMesh().
 */
function applyGemTransform(measurements: GemMeasurements, placement: GemPlacement): BufferGeometry | null {
    return _applyGemTransform(measurements, placement, 0);
}

/**
 * Same as applyGemTransform but shifts the cutter outward by CSG_PROTRUSION so
 * the boolean cutter extends through the ring surface — giving the pocket
 * opening the full girdle-width cross-section rather than just the narrow table.
 */
function applyGemCutterTransform(measurements: GemMeasurements, placement: GemPlacement): BufferGeometry | null {
    return _applyGemTransform(measurements, placement, CSG_PROTRUSION);
}

function _applyGemTransform(measurements: GemMeasurements, placement: GemPlacement, outwardOffset: number): BufferGeometry | null {
    try {
        const { gemHeightAbovePivot, geometry } = measurements;
        const { position, up, rotation } = placement;

        // outwardOffset > 0 for CSG cutters: shifts the gem outward so its volume
        // protrudes through the ring surface by that amount, ensuring a clean cut.
        const adjustedPos = position.clone().addScaledVector(up, -(gemHeightAbovePivot + SETTING_DEPTH) + outwardOffset);

        const matrix = new Matrix4().makeRotationFromQuaternion(rotation);
        matrix.setPosition(adjustedPos);

        const geo = geometry.clone().applyMatrix4(matrix);
        return geo;
    } catch {
        return null;
    }
}
